import React from 'react';
import styled from 'styled-components';
import { Box, Heading, Stack } from '@99designs/emu';
import { colors, typography } from '@99designs/emu/settings';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { Desktop } from '@99designs/spa-common';
import { royalBlue, toRightGradientVibrantBlue } from '../../utils/customColors';
import { LocalExchangeRateProvider } from './LocalExchangeRateContext';
import { RecommendationCard } from './RecommendationCard';
import { SecondaryRecommendationCard } from './RecommendationCard/SecondaryRecommendationCard';
import { RecommendationSummary } from './RecommendationSummary';
import type { CurrencyCode } from '@99designs/i18n';
import type { Product } from '../Product';

interface Props {
    recommendations: Product[];
    currency: CurrencyCode;
    exchangeRate: number;
}

const StyledHeading = styled(Heading)`
    color: ${(props) => (props.color ? props.color : colors.grey800)};
`;

const GradientText = styled.span`
    background-color: ${royalBlue};
    background-image: ${toRightGradientVibrantBlue};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    -moz-background-clip: text;
    -moz-text-fill-color: transparent;
`;

const PriceConversionDisclaimer = styled.div`
    text-align: right;
    font-size: ${typography.typeScale.size2}px;
    color: ${colors.grey700};
`;

export function Recommendations({ recommendations, currency, exchangeRate }: Props) {
    const topRecommendation = recommendations[0];
    let secondaryRecommendations: Product[] = [];
    if (recommendations.length > 1) {
        secondaryRecommendations = recommendations.slice(1, recommendations.length);
    }

    return (
        <LocalExchangeRateProvider currency={currency} exchangeRate={exchangeRate}>
            <Box marginTop="4bu">
                <StyledHeading accessibilityLevel={1} appearanceLevel={2}>
                    <FormattedReactMessage
                        message={__(
                            'To start your <GradientText>website</GradientText> today, we recommend…'
                        )}
                        replacements={{
                            GradientText: <GradientText />,
                        }}
                    />
                </StyledHeading>
            </Box>
            <Box marginTop="4bu" marginBottom="4bu">
                <Stack space="1.5bu">
                    <RecommendationCard product={topRecommendation} />
                    {secondaryRecommendations.length > 0 && (
                        <>
                            <Box paddingTop="2bu">
                                <Box paddingBottom="0.75bu">
                                    <StyledHeading accessibilityLevel={2} appearanceLevel={3}>
                                        {__('Or want more options for a customized website?')}
                                    </StyledHeading>
                                </Box>
                                <p>
                                    {__(
                                        'Get tailored quotes from up to 5 designers based on your brief and needs.'
                                    )}
                                </p>
                            </Box>
                            {secondaryRecommendations.map((secondaryRecommendation, i) => {
                                return (
                                    <SecondaryRecommendationCard
                                        key={i}
                                        product={secondaryRecommendation}
                                    />
                                );
                            })}
                            {/* eslint-disable-next-line eqeqeq */}
                            {currency != 'USD' && (
                                <PriceConversionDisclaimer>
                                    <span>{__('* Prices converted from USD.')}</span>
                                </PriceConversionDisclaimer>
                            )}
                        </>
                    )}
                </Stack>
            </Box>
            <Desktop>
                <RecommendationSummary />
            </Desktop>
        </LocalExchangeRateProvider>
    );
}
